/* eslint-disable @typescript-eslint/no-explicit-any */
import { SOCIAL_CONTENT_TEMPLATE } from './socialContentPrompts';

interface SocialContentTemplate {
	Type: string;
	'Prompt Name': string;
	'Display Name': string;
	'Prompt Text': string;
	'Formatted Prompt': string;
}

// Function to get a template by ID or randomly select one
export const getRandomTemplate = (templateId?: string) => {
	// If NO_TEMPLATE is explicitly requested, return undefined
	if (templateId === '__NO_TEMPLATE__') {
		return undefined;
	}

	if (templateId) {
		const template = (
			SOCIAL_CONTENT_TEMPLATE as SocialContentTemplate[]
		).find((t) => t['Prompt Name'] === templateId);
		if (template) {
			return template['Prompt Text'];
		}
	}
	const randomIndex = Math.floor(
		Math.random() * SOCIAL_CONTENT_TEMPLATE.length
	);
	return (SOCIAL_CONTENT_TEMPLATE[randomIndex] as SocialContentTemplate)[
		'Prompt Text'
	];
};

// Export templates for UI selection
export const TEMPLATE_OPTIONS = (
	SOCIAL_CONTENT_TEMPLATE as SocialContentTemplate[]
).map((template) => ({
	value: template['Prompt Name'],
	label: template['Display Name'],
	type: template.Type,
}));

export const FORMATTING_RULES = [
	'Use short, punchy sentences.',
	'Use emojis',
	'Keep long sentences to a minimum.',
	'Sixth grade reading level but not childish',
	'Make sure the title is bolded and with no markdown!',
	'Add line breaks between ALL paragraphs',
	'Do NOT use markdown formatting in the final output',
	'Do NOT user ** or * surrounding words in the final output',
];

const LINKEDIN_PROMPT = `Don't use "LinkedIn warriors" or similar phrases.`;

const TWITTER_PROMPT = `
1. Capture attention in the first few words
2. Share a key insight or takeaway
3. Use 1-2 relevant emojis
4. Spark curiosity or encourages engagement
5. Keep to 280 characters max`;

export const formatPromptTemplate = (
	imageAnalysis: string,
	writingAnalysis: string,
	contentHistory: any[],
	platform: string,
	markdown: string,
	templateId?: string
) => {
	// Debug logging for template selection
	console.log('Format Prompt Template Debug:', {
		templateId,
		isNoTemplate: templateId === '__NO_TEMPLATE__',
	});

	const template =
		templateId === '__NO_TEMPLATE__'
			? undefined
			: templateId
				? SOCIAL_CONTENT_TEMPLATE.find(
						(t) => t['Prompt Name'] === templateId
					)?.['Prompt Text']
				: getRandomTemplate();

	const socialTemplateSection = template
		? `<SOCIAL CONTENT TEMPLATE>\n${template}\n</SOCIAL CONTENT TEMPLATE>`
		: '';

	console.log('Formatting prompt with markdown:', {
		markdownLength: markdown?.length,
		markdownPreview: markdown?.substring(0, 100),
	});

	const prompt = `You are a gifted writer who specializes in writing social media content.

Write me a bad ass epic ${platform === 'linkedin' ? 'LinkedIn article' : 'Twitter tweet'} following this Writing Analysis:

<WRITING ANALYSIS>
${writingAnalysis}
</WRITING ANALYSIS>

${markdown ? `<NEWS ARTICLE>\n${markdown}\n</NEWS ARTICLE>` : ''}

${imageAnalysis ? `<IMAGE ANALYSIS>${imageAnalysis}</IMAGE ANALYSIS>` : ''}

${
	platform === 'twitterx'
		? `<LAST 3 TWEETS>
${contentHistory
	.slice(-3)
	.map(
		(item) =>
			`${new Date(item.createdAt).toLocaleDateString()}: ${item.content}`
	)
	.join('\n')}
</LAST 3 TWEETS>
(DO NOT CREATE SIMILAR CONTENT, BE ORIGINAL)`
		: ''
}

${platform === 'linkedin' ? `${LINKEDIN_PROMPT}` : ''}

${platform === 'twitterx' ? `${TWITTER_PROMPT}` : ''}

${FORMATTING_RULES}

${socialTemplateSection}

${
	template
		? `Please follow these steps:
1. Use the social content template provided above.
2. Identify the placeholders within that template (e.g., [Topic], [Benefit], [Outcome], etc.).
3. Write a bad ass epic ${platform === 'linkedin' ? 'LinkedIn article' : 'Twitter tweet'} following the writing analysis, 
news article content, and the social content template all together to craft a unique and engaging social media post filling in the placeholders.`
		: `Please write a bad ass epic ${platform === 'linkedin' ? 'LinkedIn article' : 'Twitter tweet'} following the writing analysis and news article content to craft a unique and engaging social media post.`
}

If a news article is provided, give a strong opinion and storytelling to make the content engaging${template ? ' while using the social content template' : ''}.`;

	console.log('Final prompt markdown section:', {
		section: markdown
			? `<NEWS ARTICLE>\n${markdown}\n</NEWS ARTICLE>`
			: 'No markdown provided',
		hasMarkdown: !!markdown,
		markdownLength: markdown?.length || 0,
	});

	return prompt;
};

// Helper to validate the prompt length
export const validatePromptLength = (prompt: string): boolean => {
	const MAX_PROMPT_LENGTH = 10000; // Adjust based on model limits
	return prompt.length <= MAX_PROMPT_LENGTH;
};
